/**
 * Scuba diving: a dive boat out to a reef (one in each country), a dive with an instructor. The first dive is a "try
 * dive": a briefing on the boat (the hand signals, clearing your ears, never holding your breath), the instructor at
 * your side the whole way, no deeper than 12 m. After three you're given your Open Water card and the limit is 18 m.
 * A logbook keeps the count, the deepest you've been and the creatures you've found; finding a new one is a thrill.
 */
import { advance, spend, type LifeEvent, type LifeState } from './life.ts';
import { localPrice } from './character.ts';

const SITES: Record<string, string> = {
  lagos: 'Coral Point Divers, Tarkwa Bay', france: 'Calanques Plongée, Marseille', us: 'Blue Reef Divers, Key Largo',
  china: 'Sanya Reef Divers, Hainan', japan: 'Kerama Blue Divers, Okinawa', italy: 'Elba Diving, Portoferraio', uae: 'Fujairah Reef Divers',
};
export const diveSiteFor = (region: string): string => SITES[region] ?? SITES.lagos;
/** the boat, the kit, the instructor, two tanks' worth of time on the reef */
export const divePrice = (s: LifeState) => localPrice(s, 45_000, 1000);

/** what there is to find on the reef (ids the game's reef places), and what the logbook calls them */
export const REEF_LIFE: Record<string, string> = {
  turtle: 'a green sea turtle', ray: 'a spotted eagle ray', clownfish: 'clownfish in their anemone', eel: 'a moray eel in its hole',
  octopus: 'an octopus', shark: 'a reef shark asleep on the sand', seahorse: 'a seahorse on the sea grass', wreck: 'the old wreck',
};

export interface DiveLog { dives: number; deepest: number; minutes: number; seen: string[]; certified: boolean }
export const diveLogOf = (s: LifeState): DiveLog => (s.diveLog ??= { dives: 0, deepest: 0, minutes: 0, seen: [], certified: false });
/** how deep you may go: a try dive stays with the instructor at 12 m; Open Water is 18 m */
export const diveLimit = (s: LifeState) => (diveLogOf(s).certified ? 18 : 12);

export function bookDive(s: LifeState): { ok: boolean; line: string } {
  if (!spend(s, divePrice(s), 'Scuba dive')) return { ok: false, line: `A dive is ${divePrice(s).toLocaleString()}. You don't have it.` };
  return { ok: true, line: diveLogOf(s).dives ? 'Your kit is on the bench, the tank already on the jacket.' : 'Your first dive. The instructor sits you down for the briefing.' };
}

/** Back on the boat: into the logbook (`seen`: what you found this dive), and what it did for you. */
export function logDive(s: LifeState, r: { minutes: number; deepest: number; seen: string[] }): { events: LifeEvent[]; lines: string[] } {
  const log = diveLogOf(s), first = log.dives === 0;
  const fresh = r.seen.filter((k) => REEF_LIFE[k] && !log.seen.includes(k));
  log.dives++; log.minutes += r.minutes; log.deepest = Math.max(log.deepest, Math.round(r.deepest * 10) / 10); log.seen.push(...fresh);
  const lines = [`Dive ${log.dives}: ${r.minutes} minutes, down to ${r.deepest.toFixed(1)} m.`];
  if (fresh.length) lines.push(`New in your logbook: ${fresh.map((k) => REEF_LIFE[k]).join(', ')}.`);
  else if (r.seen.length) lines.push(`You saw ${r.seen.map((k) => REEF_LIFE[k]).join(', ')} again.`);
  if (!log.certified && log.dives >= 3) { log.certified = true; lines.push('Three dives: the instructor signs your Open Water card. You can go down to 18 m now.'); s.history.push({ t: s.minutes, text: 'Got an Open Water diving card.' }); }
  if (first) s.history.push({ t: s.minutes, text: 'A first scuba dive.' });
  s.needs.fun = Math.min(100, s.needs.fun + 22 + fresh.length * 4);
  s.needs.energy = Math.max(0, s.needs.energy - 12);
  s.needs.hygiene = Math.max(0, s.needs.hygiene - 8);
  s.skills.fitness = Math.min(100, s.skills.fitness + 1);
  // (the dive itself, then the kit off, the boat back in)
  return { events: advance(s, r.minutes + 40, { busy: true }), lines };
}
